import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Col, Row } from 'reactstrap';
import { Form } from 'formik';
import * as PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import RouterPaths from 'constants/RouterPaths';
import FormInputField from '../../common/components/formFields/FormInputField';
import FormSelectField from '../../common/components/formFields/FormSelectField';
import FormCheckboxField from '../../common/components/formFields/FormCheckboxField';
import CommonButton from '../../common/components/CommonButton';
import { CommonButtonVariants } from '../../constants/CommonButtonVariants';
import { validateFormField } from '../../common/helpers/validateFormField';
import CommonErrorLabel from '../../common/components/CommonErrorLabel';
import { FormFieldTypes } from '../../constants/FormFieldTypes';
import { DealerPositionName } from '../../constants/DealerPosition';
import { constantToSelect } from '../../common/helpers/constantToSelect';
import { clearValidationErrors, validateMainProfile } from '../RegisterActions';
import { useMediaQuery } from '../../common/hooks/useMediaQuery';
import { scrollTop } from '../../common/helpers/generalHelper';

function AddProfileForm({ prevStep, nextStep, values }) {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [isFieldEmpty, setFieldEmpty] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const validateErrors = useSelector((state) => state.register.get('validateErrors'));

  const dealerRole = useMemo(() => constantToSelect(DealerPositionName), []);

  useEffect(() => {
    scrollTop();
  }, []);

  const clearValidationErrorsCallback = useCallback(
    () => dispatch(clearValidationErrors()),
    [dispatch],
  );

  useEffect(() => clearValidationErrorsCallback, [clearValidationErrorsCallback]);

  const checkIsFieldEmpty = useCallback(() => {
    const { firstName, lastName, email, position, password, passwordRepeat, acceptTos } =
      values;
    const valuesArray = [
      firstName,
      lastName,
      email,
      position,
      password,
      passwordRepeat,
      acceptTos,
    ];
    const isFieldEmpty = valuesArray.some((item) => !item);
    return setFieldEmpty(isFieldEmpty);
  }, [values]);

  useEffect(() => checkIsFieldEmpty(), [checkIsFieldEmpty]);

  const [errors, setErrors] = useState({
    firstNameError: '',
    lastNameError: '',
    emailError: '',
    positionError: '',
    passwordError: '',
    passwordRepeatError: '',
    acceptTosError: '',
  });

  const validateForm = useCallback(
    () =>
      new Promise((resolve, reject) => {
        const { firstName, lastName, email, position, password, passwordRepeat, acceptTos } =
          values;

        const formErrors = {
          firstNameError: validateFormField(firstName, FormFieldTypes.STRING, true, 50),
          lastNameError: validateFormField(lastName, FormFieldTypes.STRING, true, 50),
          emailError: validateFormField(email, FormFieldTypes.EMAIL, true, 50),
          positionError: validateFormField(position, FormFieldTypes.ENUM, true),
          passwordError: validateFormField(
            password,
            FormFieldTypes.PASSWORD,
            true,
            null,
            6,
            passwordRepeat,
          ),
          passwordRepeatError: validateFormField(
            passwordRepeat,
            FormFieldTypes.PASSWORD,
            true,
            null,
            6,
            password,
          ),
          acceptTosError: validateFormField(acceptTos, FormFieldTypes.BOOLEAN, true),
        };

        setErrors(formErrors);

        if (Object.values(formErrors).some((error) => error)) {
          reject(formErrors);
        } else {
          resolve();
        }
      }),
    [values, setErrors],
  );

  useEffect(() => {
    if (submitted) {
      validateForm().catch(() => {});
    }
  }, [values, submitted, validateForm]);

  const moveToNextStep = useCallback(() => {
    setSubmitted(true);
    validateForm()
      .then(() => dispatch(validateMainProfile(values, () => nextStep())))
      .catch((errs) => {
        if (isMobile) {
          const firstErrorField = Object.keys(errs).find((key) => errs[key]);
          if (firstErrorField) {
            const fieldName = firstErrorField.replace('Error', '');
            const element = document.getElementsByName(fieldName)[0];
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }
        }
      });
  }, [validateForm, dispatch, values, nextStep, isMobile]);

  const {
    firstNameError,
    lastNameError,
    emailError,
    positionError,
    passwordError,
    passwordRepeatError,
    acceptTosError,
  } = errors;

  const headerStyle = useMemo(() => ({ borderBottom: '2px solid #F1001E' }), []);

  const tosLabel = useMemo(
    () => (
      <label className="label-required">
        I accept the{' '}
        <a
          href={RouterPaths.PRIVACY_POLICY}
          className="color-green"
          rel="noreferrer"
          target="_blank"
        >
          Privacy Policy
        </a>{' '}
        and{' '}
        <a
          href={RouterPaths.TERMS_AND_CONDITIONS}
          className="color-green"
          rel="noreferrer"
          target="_blank"
        >
          Terms & Conditions
        </a>
      </label>
    ),
    [],
  );
  return (
    <Col
      md="12"
      className="register-form-container d-flex flex-column justify-content-center align-items-center"
    >
      <Row md={12} className="register-form-container__title w-100">
        <Col md={12}>
          <h2 className="font-weight-bold" style={headerStyle}>
            User details
          </h2>
        </Col>
      </Row>
      <Row
        md={12}
        className="register-form-container__content justify-content-center mt-5 w-100"
      >
        <Form autoComplete="off" className="register-form__profile-details">
          <Row className="justify-content-between mb-3">
            <Col md="6">
              <div className="p-1 mobile-margin">
                <FormInputField
                  name="firstName"
                  label="First name"
                  invalid={!!firstNameError}
                  required
                  labelVariant="bordered"
                />
                <CommonErrorLabel value={firstNameError} />
              </div>
            </Col>
            <Col md="6">
              <div className="p-1">
                <FormInputField
                  name="lastName"
                  label="Last name"
                  invalid={!!lastNameError}
                  required
                  labelVariant="bordered"
                />
                <CommonErrorLabel value={lastNameError} />
              </div>
            </Col>
          </Row>
          <Row className="justify-content-between mb-3">
            <Col md="6">
              <div className="p-1 mobile-margin">
                <FormSelectField
                  name="position"
                  label="Position"
                  options={dealerRole}
                  required
                  labelVariant="bordered"
                />
                <CommonErrorLabel value={positionError} />
                <FormSelectField rowClassNames="form-select-field-hidden" />
              </div>
            </Col>
            <Col md="6">
              <div className="p-1">
                <FormInputField
                  name="email"
                  label="E-mail address"
                  invalid={!!emailError || validateErrors?.email}
                  required
                  labelVariant="bordered"
                />
                <CommonErrorLabel value={emailError || validateErrors?.email} />
              </div>
            </Col>
          </Row>
          <Row className="justify-content-between mb-3">
            <Col md="6">
              <div className="p-1 mobile-margin">
                <FormInputField
                  type="password"
                  name="password"
                  label="Password"
                  invalid={!!passwordError}
                  required
                  labelVariant="bordered"
                  showPasswordIcon={true}
                />
                <CommonErrorLabel value={passwordError} />
              </div>
            </Col>
            <Col md="6">
              <div className="p-1">
                <FormInputField
                  type="password"
                  name="passwordRepeat"
                  label="Repeat password"
                  invalid={!!passwordRepeatError}
                  required
                  labelVariant="bordered"
                  showPasswordIcon={true}
                />
                <CommonErrorLabel value={passwordRepeatError} />
              </div>
            </Col>
          </Row>
          <Row>
            <Col md="12">
              <div className="p-1 w-100">
                <FormCheckboxField label={tosLabel} name="acceptTos" id="acceptTos" />
                <CommonErrorLabel value={acceptTosError} />
              </div>
            </Col>
          </Row>
        </Form>
      </Row>
      <Row md={12} className="register-form-container__title w-100 mt-4">
        <Col md="12">
          <div>
            <h2 className="font-weight-bold">Marketing preferences</h2>
            <p className="marketing-description mb-0 p-0">
              Choose how you want to hear about the latest products, services and offers from
              us. By selecting the below boxes, you&apos;re consenting to receive updates from
              The Stock Vault, via the communication methods you choose. We may occasionally
              pass on your details to organisation we think you might be interested in, but
              only people we trust and work closely with.
            </p>
          </div>
          <Row className="mt-3 d-flex flex-wrap">
            <Col style={{ cursor: 'pointer' }} sm="6" md="3">
              <FormCheckboxField
                label="Phone"
                name="phoneMarketingAllowed"
                id="phoneMarketingAllowed"
              />
            </Col>
            <Col style={{ cursor: 'pointer' }} sm="6" md="3">
              <FormCheckboxField
                label="E-mail"
                name="emailMarketingAllowed"
                id="emailMarketingAllowed"
              />
            </Col>
            <Col style={{ cursor: 'pointer' }} sm="6" md="3">
              <FormCheckboxField
                label="SMS"
                name="smsMarketingAllowed"
                id="smsMarketingAllowed"
              />
            </Col>
            <Col style={{ cursor: 'pointer' }} sm="6" md="3">
              <FormCheckboxField
                label="Post"
                name="postMarketingAllowed"
                id="postMarketingAllowed"
              />
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Navigation Buttons */}
      <Row className="w-100 justify-content-between mt-4">
        <Col md="6" xs="6">
          <CommonButton
            label="PREVIOUS"
            variant={CommonButtonVariants.OUTLINED_RED}
            handleClick={prevStep}
            className="w-100"
          />
        </Col>
        <Col md="6" xs="6">
          <CommonButton
            label="NEXT"
            variant={CommonButtonVariants.PRIMARY_RED}
            handleClick={moveToNextStep}
            disabled={isFieldEmpty}
            className="w-100"
          />
        </Col>
      </Row>
    </Col>
  );
}

AddProfileForm.propTypes = {
  prevStep: PropTypes.func.isRequired,
  nextStep: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
};

export default AddProfileForm;
